import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { 
  UserPlus, 
  Search, 
  FileText, 
  CheckCircle, 
  Upload, 
  CreditCard,
  ArrowRight,
  Building2,
  Target,
  Users,
  ClipboardCheck,
  BadgeCheck,
  Wallet
} from 'lucide-react';

const ProcessFlow = () => {
  const [activeTab, setActiveTab] = useState('seller');

  const sellerSteps = [
    {
      id: 1,
      icon: UserPlus,
      title: 'Sign Up as Seller',
      description: 'Register with your business email and company details. Get verified within 24-48 hours.',
      color: 'from-orange-400 to-orange-500',
    },
    {
      id: 2,
      icon: Search,
      title: 'Browse Campaigns',
      description: 'Explore available buyer campaigns. Filter by industry, region, lead type, and budget.',
      color: 'from-orange-500 to-orange-600',
    },
    {
      id: 3,
      icon: Target,
      title: 'Book Campaign',
      description: 'Select lead types (HQL, MQL, SQL), commit quantities, and set your delivery timeline.',
      color: 'from-amber-500 to-orange-500',
    },
    {
      id: 4,
      icon: Upload,
      title: 'Deliver Leads',
      description: 'Upload verified leads through our platform. Ensure data quality and compliance.',
      color: 'from-orange-500 to-amber-500',
    },
    {
      id: 5,
      icon: ClipboardCheck,
      title: 'Lead Validation',
      description: 'Leads are validated against buyer criteria. Invalid leads can be replaced.',
      color: 'from-orange-400 to-orange-500',
    },
    {
      id: 6,
      icon: Wallet,
      title: 'Get Paid',
      description: 'Receive payment for approved leads. Track earnings in your dashboard.',
      color: 'from-orange-500 to-orange-600',
    },
  ];

  const buyerSteps = [
    {
      id: 1,
      icon: UserPlus,
      title: 'Sign Up as Buyer',
      description: 'Register with your business email. Define your company profile and lead requirements.',
      color: 'from-slate-600 to-slate-700',
    },
    {
      id: 2,
      icon: FileText,
      title: 'Create Campaign',
      description: 'Define target criteria: industry, job titles, company size, regions, and lead types needed.',
      color: 'from-slate-700 to-slate-800',
    },
    {
      id: 3,
      icon: Users,
      title: 'Connect with Sellers',
      description: 'Browse verified sellers, review their profiles, and send connection requests.',
      color: 'from-slate-600 to-slate-700',
    },
    {
      id: 4,
      icon: CheckCircle,
      title: 'Review Bookings',
      description: 'Receive booking requests from sellers. Review commitments and approve partnerships.',
      color: 'from-slate-700 to-slate-800',
    },
    {
      id: 5,
      icon: BadgeCheck,
      title: 'Receive & Validate Leads',
      description: 'Get delivered leads in your dashboard. Validate quality and request replacements if needed.',
      color: 'from-slate-600 to-slate-700',
    },
    {
      id: 6,
      icon: CreditCard,
      title: 'Process Payment',
      description: 'Pay only for validated, quality leads. Transparent pricing with no hidden fees.',
      color: 'from-slate-700 to-slate-800',
    },
  ];

  const steps = activeTab === 'seller' ? sellerSteps : buyerSteps;

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-700 text-sm font-semibold rounded-full mb-4">
            Complete Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            From Signup to Payment
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A transparent, step-by-step journey for both buyers and sellers
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-slate-100 p-1.5 rounded-xl">
            <button
              onClick={() => setActiveTab('seller')}
              className={cn(
                "px-8 py-3 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center gap-2",
                activeTab === 'seller'
                  ? "bg-orange-500 text-white shadow-lg shadow-orange-500/30"
                  : "text-slate-600 hover:text-slate-900"
              )}
            >
              <Building2 className="w-4 h-4" />
              Seller Journey
            </button>
            <button
              onClick={() => setActiveTab('buyer')}
              className={cn(
                "px-8 py-3 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center gap-2",
                activeTab === 'buyer'
                  ? "bg-slate-700 text-white shadow-lg shadow-slate-700/30"
                  : "text-slate-600 hover:text-slate-900"
              )}
            >
              <Users className="w-4 h-4" />
              Buyer Journey
            </button>
          </div>
        </div>

        {/* Process Flow */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className="relative group"
                >
                  {/* Connection Line for Desktop */}
                  {index < steps.length - 1 && index !== 2 && (
                    <div className="hidden lg:block absolute top-12 left-full w-6 z-10">
                      <ArrowRight className={cn(
                        "w-6 h-6 -ml-3",
                        activeTab === 'seller' ? 'text-orange-300' : 'text-slate-400'
                      )} />
                    </div>
                  )}
                  
                  {/* Card */}
                  <div className={cn(
                    "bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:border-transparent transition-all duration-300 h-full",
                    "hover:-translate-y-1"
                  )}>
                    {/* Step Number & Icon */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className={cn(
                        "w-14 h-14 rounded-xl bg-gradient-to-br flex items-center justify-center text-white shadow-lg",
                        step.color
                      )}>
                        <Icon className="w-7 h-7" />
                      </div>
                      <div className={cn(
                        "text-4xl font-bold",
                        activeTab === 'seller' ? 'text-orange-100' : 'text-slate-200'
                      )}>
                        {String(step.id).padStart(2, '0')}
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-500 mb-4">Ready to get started?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openSignupDialog', { detail: { type: 'seller' } }))}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-orange-500/30"
            >
              Start as Seller
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openSignupDialog', { detail: { type: 'buyer' } }))}
              className="px-6 py-3 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-slate-700/30"
            >
              Start as Buyer
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessFlow;
